import { Z_INDEX } from "../constants"
import { useResolvedTheme } from "../hooks/useResolvedTheme"
import NiceModal from "@ebay/nice-modal-react"
import { Mic, MicOff, PhoneOff, X } from "lucide-react"
import { observer } from "mobx-react"
import { useLayoutEffect, useState, useSyncExternalStore } from "react"
import { matchPath } from "react-router"
import { bindControlHost, controlHost } from "../appControls/bridge"
import { useComputers } from "../computers/context"
import { hasVoiceBridge } from "../electronAPIs/voice"
import { codeRoutes, useCodeNavigate } from "../routing"
import { useCodeStore } from "../store/context"
import { SettingsModal } from "../components/settings/SettingsModal"
import { voiceManager } from "./manager"

export const VoiceButton = observer(function VoiceButton() {
    const store = useCodeStore()
    const state = useSyncExternalStore(voiceManager.subscribe, voiceManager.snapshot)
    const active = ["connecting", "live", "closing"].includes(state.phase)
    if (store.isGuest) return null
    return (
        <button
            type="button"
            className={`btn flex h-8 w-8 flex-none items-center justify-center ${active ? "text-primary" : "text-muted hover:text-base-content"}`}
            aria-label={active ? "End conversation" : "Start conversation"}
            title={active ? "End conversation" : "Conversation mode"}
            onClick={() => {
                if (active) voiceManager.end()
                else if (hasVoiceBridge()) void voiceManager.start(store.personalSettingsStore?.settings.current.voiceName ?? "gleam")
                else void NiceModal.show(SettingsModal, { store, initialTab: "voice" })
            }}
        >
            <Mic size={16} />
        </button>
    )
})

export const VoiceSurface = observer(function VoiceSurface() {
    const store = useCodeStore()
    const computers = useComputers()
    const navigate = useCodeNavigate()
    const workspaceId = matchPath({ path: codeRoutes.CodeWorkspace.path, end: false }, navigate.location.pathname)?.params.workspaceId
    const matchedTask = matchPath(codeRoutes.CodeWorkspaceTask.path, navigate.location.pathname)?.params.taskId
    const taskId = matchedTask === "create" ? undefined : matchedTask
    useLayoutEffect(() => {
        const unbind = bindControlHost({ store, computers, navigate, workspaceId, taskId })
        voiceManager.contextChanged()
        return unbind
    }, [store, computers, navigate, workspaceId, taskId])
    return null
})

/** Mounted above App so microphone/end controls also survive offline/reconnect screens. */
export const VoiceCallPanel = observer(function VoiceCallPanel() {
    const state = useSyncExternalStore(voiceManager.subscribe, voiceManager.snapshot)
    const [panelError, setPanelError] = useState("")
    let host: ReturnType<typeof controlHost> | undefined
    try {
        host = controlHost()
    } catch {
        /* Call controls work without an active node UI. */
    }
    const theme = useResolvedTheme(host?.store.personalSettingsStore?.settings.current.theme ?? "system")
    if (state.phase === "idle" && !state.controller && !state.error) return null
    const active = state.phase === "live" || state.phase === "connecting" || state.phase === "closing"
    const openController = async () => {
        const controller = state.controller
        if (!controller) return
        try {
            const current = controlHost()
            if (controller.nodeId !== current.computers.state.activeNodeId && !(await current.computers.selectScope(controller.nodeId)))
                throw new Error("Could not open the controller's computer")
            current.navigate.go("CodeWorkspaceTask", { workspaceId: controller.workspaceId, taskId: controller.taskId })
            setPanelError("")
        } catch (error) {
            setPanelError(error instanceof Error ? error.message : "Could not open the controller")
        }
    }
    return (
        <aside
            aria-label="Voice conversation"
            className={`code-theme ${theme} fixed bottom-4 right-4 w-[min(390px,calc(100vw-2rem))] border border-border bg-base-100 p-3 text-sm text-base-content shadow-xl`}
            style={{ zIndex: Z_INDEX.GLOBAL_MODAL + 10 }}
        >
            <div className="flex items-center gap-2">
                <Mic size={16} className={state.phase === "live" ? "text-primary" : "text-muted"} />
                <strong className="flex-1">
                    {state.phase === "live"
                        ? "Conversation mode"
                        : state.phase === "connecting"
                          ? "Connecting…"
                          : state.phase === "closing"
                            ? "Ending call…"
                            : "Call ended"}
                </strong>
                {state.phase === "live" ? (
                    <button
                        type="button"
                        className="btn p-2"
                        aria-label={state.muted ? "Unmute microphone" : "Mute microphone"}
                        onClick={() => voiceManager.toggleMute()}
                    >
                        {state.muted ? <MicOff size={16} /> : <Mic size={16} />}
                    </button>
                ) : null}
                <button
                    type="button"
                    className="btn p-2 text-muted hover:text-error"
                    aria-label={active ? "End call" : "Dismiss voice panel"}
                    onClick={() => (active ? voiceManager.end() : voiceManager.dismiss())}
                >
                    {active ? <PhoneOff size={16} /> : <X size={16} />}
                </button>
            </div>
            <p className="mt-1 text-xs text-muted">
                {state.muted && active ? "Microphone muted · " : ""}
                {Math.floor(state.seconds / 60)}:{String(Math.floor(state.seconds % 60)).padStart(2, "0")} ·{" "}
                {state.working
                    ? `${state.working} request(s) working`
                    : state.phase === "live"
                      ? "Listening"
                      : state.finalized
                        ? "Usage finalized"
                        : "Final usage unconfirmed"}
            </p>
            {state.controller ? (
                <button type="button" className="btn mt-2 text-xs text-primary" onClick={() => void openController()}>
                    Open voice Admin task
                </button>
            ) : null}
            {state.confirmation ? (
                <p role="status" className="mt-2 border border-border p-2 text-xs">
                    {state.confirmation.detail}
                    <br />
                    Say “{state.confirmation.phrase}” or “cancel”.
                </p>
            ) : null}
            {state.audioBlocked ? (
                <button type="button" className="btn mt-2 border border-border p-2" onClick={() => void voiceManager.resumeAudio()}>
                    Play assistant audio
                </button>
            ) : null}
            {panelError ? (
                <p role="alert" className="mt-2 text-xs text-error">
                    {panelError}
                </p>
            ) : null}
            {state.error ? (
                <p role="alert" className="mt-2 text-xs text-error">
                    {state.error}
                </p>
            ) : null}
            {state.phase === "error" && host ? (
                <button
                    type="button"
                    className="btn mt-2 text-xs text-primary"
                    onClick={() => {
                        const current = controlHost()
                        void NiceModal.show(SettingsModal, { store: current.store, initialTab: "voice" })
                    }}
                >
                    Voice settings
                </button>
            ) : null}
            {state.captions.length ? (
                <details className="mt-2" open>
                    <summary className="cursor-pointer text-xs text-muted">Captions</summary>
                    <div className="mt-2 max-h-40 overflow-auto text-xs">
                        {(["user", "assistant"] as const).map((speaker) => (
                            <p key={speaker} className="mb-2 whitespace-pre-wrap">
                                <strong>{speaker === "user" ? "You" : "Voice"}: </strong>
                                {state.captions
                                    .filter((fragment) => fragment.speaker === speaker)
                                    .slice(-35)
                                    .map((fragment) => fragment.text)
                                    .join("")}
                            </p>
                        ))}
                    </div>
                </details>
            ) : null}
            {!active && state.controller ? <p className="mt-2 text-xs text-muted">Accepted work continues in its task.</p> : null}
        </aside>
    )
})
